<script lang="ts" setup>
import {computed, reactive, ref} from 'vue'
import {storeToRefs} from 'pinia'
import {useAppStore} from '@/stores/app'
import {useLocalStore} from '@/stores/local'
import type {TaskInstance, TaskWithInstance} from '@backend/types'
import {Input} from '@/components/ui/input'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {Separator} from '@/components/ui/separator'
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from '@/components/ui/collapsible'
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group'
import {Table, TableBody} from '@/components/ui/table'
import TaskItem from '@/components/TaskItem.vue'
import {Search, Loader2, ChevronDown, ChevronRight, Rows2, Rows3, Rows4} from 'lucide-vue-next'

const appStore = useAppStore()
const {searchResults, isSearching} = storeToRefs(appStore)

const localStore = useLocalStore()
const {viewMode} = storeToRefs(localStore)

const searchQuery = ref('')
const hasSearched = ref(false)

const collapsedGroups = reactive<Record<string, boolean>>({
  done: true,
})

const handleSearch = async () => {
  if (!searchQuery.value.trim()) return
  hasSearched.value = true
  await appStore.searchTasks(searchQuery.value)
}

const groupedTasks = computed(() => {
  const groups: Record<string, {task: TaskWithInstance; taskInstance: TaskInstance | null}[]> = {}

  for (const task of searchResults.value) {
    if (!task.instances.length) {
      const item = {task, taskInstance: null}
      const folder = 'pending'
      if (!groups[folder]) groups[folder] = []
      groups[folder].push(item)
    } else {
      for (const instance of task.instances) {
        const item = {task, taskInstance: instance}
        if (instance.done) {
          if (!groups['done']) groups['done'] = []
          groups['done'].push(item)
        } else {
          const folder = task.folder || 'No Folder'
          if (!groups[folder]) groups[folder] = []
          groups[folder].push(item)
        }
      }
    }
  }

  return Object.entries(groups)
    .filter(([_, tasks]) => tasks.length > 0)
    .sort(([a], [b]) => {
      if (a === 'done') return 1
      if (b === 'done') return -1
      if (a === 'pending') return 1
      if (b === 'pending') return -1
      return a.localeCompare(b)
    })
})
</script>

<template>
  <!-- Header: title + view mode toggle via Teleport -->
  <Teleport to="#page-header">
    <div class="flex flex-1 items-center justify-between">
      <h1 class="truncate text-lg font-semibold">{{ $t('actions.search') }}</h1>
      <ToggleGroup
        type="single"
        :model-value="viewMode"
        @update:model-value="
          (v: any) => {
            if (v) viewMode = v as typeof viewMode
          }
        ">
        <ToggleGroupItem value="compact" size="sm" class="h-7 w-7 p-0">
          <Rows2 class="h-3.5 w-3.5" />
        </ToggleGroupItem>
        <ToggleGroupItem value="normal" size="sm" class="h-7 w-7 p-0">
          <Rows3 class="h-3.5 w-3.5" />
        </ToggleGroupItem>
        <ToggleGroupItem value="detailed" size="sm" class="h-7 w-7 p-0">
          <Rows4 class="h-3.5 w-3.5" />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  </Teleport>

  <!-- Main content -->
  <div class="flex flex-1 flex-col">
    <!-- Search bar -->
    <div class="flex items-center gap-2 p-3">
      <Input
        v-model="searchQuery"
        :placeholder="$t('messages.search_placeholder')"
        class="flex-1"
        @keydown.enter="handleSearch" />
      <Button size="icon" :disabled="isSearching" @click="handleSearch">
        <Loader2 v-if="isSearching" class="h-4 w-4 animate-spin" />
        <Search v-else class="h-4 w-4" />
      </Button>
    </div>

    <Separator />

    <!-- Results area -->
    <div class="flex-1">
      <!-- Loading state -->
      <div v-if="isSearching" class="flex h-40 items-center justify-center gap-2 text-muted-foreground">
        <Loader2 class="h-4 w-4 animate-spin" />
        {{ $t('messages.searching') }}
      </div>

      <!-- Results -->
      <template v-else-if="groupedTasks.length > 0">
        <Collapsible
          v-for="[groupName, tasks] in groupedTasks"
          :key="groupName"
          :open="!collapsedGroups[groupName]"
          @update:open="(val: boolean) => (collapsedGroups[groupName] = !val)"
          class="mb-1">
          <CollapsibleTrigger as-child>
            <button class="flex w-full items-center gap-1.5 px-3 py-2 text-sm font-medium hover:bg-accent/50">
              <ChevronRight v-if="collapsedGroups[groupName]" class="h-4 w-4 shrink-0 text-muted-foreground" />
              <ChevronDown v-else class="h-4 w-4 shrink-0 text-muted-foreground" />
              {{ $t('scopes.' + groupName) }}
              <Badge variant="secondary" class="ml-1 h-5 px-1.5 text-xs">{{ tasks.length }}</Badge>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Table class="table-fixed">
              <colgroup>
                <col class="w-10" />
                <col />
                <col v-if="viewMode !== 'compact'" class="w-24" />
                <col class="w-10" />
              </colgroup>
              <TableBody>
                <TaskItem
                  v-for="item in tasks"
                  :key="item.taskInstance?.task_instance_id ?? item.task.task_id"
                  :task="item.task"
                  :task-instance="item.taskInstance"
                  :view-mode="viewMode" />
              </TableBody>
            </Table>
          </CollapsibleContent>
        </Collapsible>
      </template>

      <!-- No results (only after first search) -->
      <div v-else-if="hasSearched && !isSearching" class="flex h-40 items-center justify-center text-muted-foreground">
        {{ $t('messages.no_results') }}
      </div>
    </div>
  </div>
</template>
